import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Button, Navbar, Nav, NavDropdown } from 'react-bootstrap';
import './components/styles/colors.css';
import { BrowserRouter as Router, Link } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { Helmet } from 'react-helmet';
import LoadingSpinner from './components/LoadingSpinner';
import LoginModal from './components/LoginModal';
import PatientsListTab from './components/PatientsListTab';
import { fetchAllPatients, fetchPatientById, fetchStudyPipeline, startPatientListPrefetch, resetPatientWorkspacePrefetch, getDatabaseMode, setDatabaseMode, applyDatabaseLaunchPath, syncDatabaseModePath, prepareDemoSession, clearDemoSessionResetFlag, enrollPatientInStudies, DATABASE_MODES } from './api';
import { runAfterPaint } from './utils/runAfterPaint';
import DatabaseModeToggle from './components/DatabaseModeToggle';
import CompassWorkspace from './components/compass/CompassWorkspace';
import AllianceWorkspace from './components/alliance/AllianceWorkspace';
import StudyConsentDialog from './components/StudyConsentDialog';
import { studyConsentHandoff } from './studies/studyEligibility';
import { flushOpenDemoCharts } from './studies/studyChartStore';
import { effectiveAppointmentType } from './utils/effectiveAppointmentType';
import logo from './components/icons/square_logo.png';

function patientHasScheduledSurgery(patient, patients) {
  if (!patient?.id) return false;
  const samePatient = (row) => String(row?.id) === String(patient.id);
  if ((patients || []).some((row) => samePatient(row) && effectiveAppointmentType(row) === 'SURGERY')) {
    return true;
  }
  return effectiveAppointmentType(patient) === 'SURGERY';
}

function navTabStyle(selected) {
  return {
    padding: '0.35rem 0.75rem',
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: '0.25rem',
    marginLeft: '0.25rem',
    backgroundColor: selected ? 'var(--navbar-toggle-selected)' : 'transparent',
    color: 'var(--brand-primary-dark)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };
}

function App() {
  const [allPatients, setAllPatients] = useState([]);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingPatients, setPendingPatients] = useState([...Array(10)].map((_, i) => ({
    id: i + 1,
    status: 'pending'
  })));
  const [approvedPatients, setApprovedPatients] = useState([]);
  const [showSurgeryOnly, setShowSurgeryOnly] = useState(false); // false = Joint Decisions, true = Med Management (surgery)
  const [study, setStudy] = useState(() => {
    const view = new URLSearchParams(window.location.search).get('view');
    if (view === 'alliance') return 'alliance';
    if (view === 'compass') return 'compass';
    return '';
  });
  const showCompass = study === 'compass' || study === 'alliance';
  const prospectStudyRef = useRef('alliance');
  const [studyHandoff, setStudyHandoff] = useState(null);
  const [consentOffer, setConsentOffer] = useState(null);
  const [enrollError, setEnrollError] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [pipelineReload, setPipelineReload] = useState(0);
  const [enrollmentSurgeryPrompt, setEnrollmentSurgeryPrompt] = useState(0);
  const studyHandoffPatientRef = useRef(null);
  const prospectOfferSeq = useRef(0);
  const prospectOfferKey = useRef('');
  const dismissedOfferKey = useRef('');
  const [databaseMode, setDatabaseModeState] = useState(() => applyDatabaseLaunchPath());
  const [blurPatientNames, setBlurPatientNames] = useState(() => getDatabaseMode() === DATABASE_MODES.LIVE);
  const [isPatientListLoading, setIsPatientListLoading] = useState(true);
  const allPatientsRef = useRef([]);
  allPatientsRef.current = allPatients;
  const hadAuthenticatedSession = useRef(false);
  const databaseModeRef = useRef(databaseMode);
  databaseModeRef.current = databaseMode;

  // Performance timing
  const pageLoadStartTime = performance.now();
  console.log('⏱️ [PERFORMANCE] Page load started at:', new Date().toISOString());

  // Add auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const authCheckTime = performance.now();
      const authCheckDuration = authCheckTime - pageLoadStartTime;
      console.log('⏱️ [PERFORMANCE] Auth check completed in:', authCheckDuration.toFixed(2), 'ms');

      if (nextUser) {
        hadAuthenticatedSession.current = true;
        setUser(nextUser);
        setIsLoggedIn(true);
      } else if (hadAuthenticatedSession.current) {
        hadAuthenticatedSession.current = false;
        resetPatientWorkspacePrefetch();
        clearDemoSessionResetFlag();
        try {
          localStorage.removeItem('authToken');
        } catch (error) {
          // ignore storage errors
        }
        allPatientsRef.current = [];
        setAllPatients([]);
        setCurrentPatient(null);
        setUser(null);
        setIsLoggedIn(false);
        if (process.env.NODE_ENV !== 'test') {
          window.location.reload();
        }
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

    // Fetch all patients
    useEffect(() => {
        async function loadPatients() {
            if (!isLoggedIn) return;
            const modeAtStart = databaseMode;
            setIsPatientListLoading(true);
            
            const fetchStartTime = performance.now();
            console.log('⏱️ [PERFORMANCE] Starting to fetch patients...');

            if (modeAtStart === DATABASE_MODES.DEMO) {
              await prepareDemoSession();
              if (databaseModeRef.current !== modeAtStart) return;
            }
            
            const patientsData = await fetchAllPatients();
            if (databaseModeRef.current !== modeAtStart) return;
            
            const fetchEndTime = performance.now();
            const fetchDuration = fetchEndTime - fetchStartTime;
            console.log('⏱️ [PERFORMANCE] Patient data fetched in:', fetchDuration.toFixed(2), 'ms');
            
            setAllPatients(patientsData || []);
            setIsPatientListLoading(false);
            
            // Calculate total time from page load to patients displayed
            const totalTime = fetchEndTime - pageLoadStartTime;
            console.log('⏱️ [PERFORMANCE] Total time from page load to patients ready:', totalTime.toFixed(2), 'ms');
            console.log('⏱️ [PERFORMANCE] Number of patients loaded:', (patientsData || []).length);
        }
        loadPatients();
    }, [isLoggedIn, databaseMode]);

  useEffect(() => {
    setBlurPatientNames(databaseMode === DATABASE_MODES.LIVE);
  }, [databaseMode]);

  useEffect(() => {
    if (!isLoggedIn || !allPatients.length) return undefined;
    return runAfterPaint(() => {
      startPatientListPrefetch(allPatients, showSurgeryOnly ? 'mm' : 'jd');
    });
  }, [isLoggedIn, allPatients, showSurgeryOnly]);

  // Fetch current patient based on patientId
  const loadCurrentPatient = useCallback(async (patientId, appointmentId) => {
    const patientData = await fetchPatientById(patientId);
    const base = patientData && typeof patientData === 'object' ? { ...patientData } : {};
    const wantedAid =
      appointmentId != null && appointmentId !== '' ? String(appointmentId) : null;
    const fromList = allPatientsRef.current.find((p) => {
      if (p.id !== patientId) return false;
      if (wantedAid == null) return true;
      return p.appointment_id != null && String(p.appointment_id) === wantedAid;
    });
    const aid = wantedAid ?? fromList?.appointment_id ?? base.appointment_id;
    if (aid != null && aid !== '') {
      base.appointment_id = typeof aid === 'string' ? aid : String(aid);
    }
    if (fromList?.surgery != null) base.surgery = fromList.surgery;
    if (fromList?.appointment_type != null) base.appointment_type = fromList.appointment_type;
    if (fromList?.surgery_start_time != null) base.surgery_start_time = fromList.surgery_start_time;
    setCurrentPatient(Object.keys(base).length ? base : {});
  }, []);

  const refreshPatientList = async () => {
    const patientsData = await fetchAllPatients();
    const next = patientsData || [];
    allPatientsRef.current = next;
    setAllPatients(next);
    return next;
  };

  const handleLoginSuccess = (user) => {
    setUser(user);
    setIsLoggedIn(true);
  };

  const handleDatabaseModeChange = (mode) => {
    flushOpenDemoCharts();
    const next = setDatabaseMode(mode);
    if (next === databaseMode) return;
    if (next === DATABASE_MODES.DEMO) {
      clearDemoSessionResetFlag();
    }
    syncDatabaseModePath(next);
    setDatabaseModeState(next);
    setIsPatientListLoading(true);
    resetPatientWorkspacePrefetch();
    setCurrentPatient(null);
    setStudyHandoff(null);
    setConsentOffer(null);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      flushOpenDemoCharts();
      clearDemoSessionResetFlag();
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Add global style for background color
  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(210, 50%, 99%)';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    prospectOfferSeq.current += 1;
    const id = currentPatient?.id ?? null;
    if (studyHandoffPatientRef.current && id && studyHandoffPatientRef.current !== id) {
      setStudyHandoff(null);
      setConsentOffer(null);
    }
    studyHandoffPatientRef.current = id;
  }, [currentPatient?.id]);

  const offerEnrollmentOnProspect = useCallback(async (patientId, seq, studyId) => {
    try {
      const result = await fetchStudyPipeline(studyId);
      const offerKey = `${patientId}:${studyId}`;
      if (seq !== prospectOfferSeq.current || prospectOfferKey.current !== offerKey) return;
      if (dismissedOfferKey.current === offerKey) return;
      const activelyEnrolled = (result?.patients || []).some((row) => (
        String(row.id) === String(patientId) && !row.declined
      ));
      if (activelyEnrolled) return;
      setEnrollError('');
      setConsentOffer(studyConsentHandoff());
    } catch {
      // Leave the popup closed when enrollment status cannot be read.
    }
  }, []);

  useEffect(() => {
    if (!showCompass || !currentPatient?.id || (study !== 'compass' && study !== 'alliance')) {
      prospectOfferKey.current = '';
      dismissedOfferKey.current = '';
      return;
    }
    const key = `${currentPatient.id}:${study}`;
    if (prospectOfferKey.current === key) return;
    prospectOfferKey.current = key;
    offerEnrollmentOnProspect(currentPatient.id, prospectOfferSeq.current, study);
  }, [currentPatient?.id, offerEnrollmentOnProspect, showCompass, study]);

  const acceptStudyEnrollment = useCallback(async (studyIds) => {
    const patientId = currentPatient?.id;
    const chosen = (consentOffer?.studies || []).filter((study) => studyIds.includes(study.id));
    if (!patientId || !chosen.length) return;
    if (!consentOffer?.surgeryDate && !patientHasScheduledSurgery(currentPatient, allPatientsRef.current)) {
      setConsentOffer(null);
      setEnrollError('');
      setEnrollmentSurgeryPrompt((value) => value + 1);
      return;
    }
    setEnrolling(true);
    setEnrollError('');
    try {
      await enrollPatientInStudies(patientId, chosen.map((study) => study.id));
      setConsentOffer(null);
      setPipelineReload((value) => value + 1);
      if (chosen.length === 1) {
        setShowSurgeryOnly(false);
        setStudy(chosen[0].id);
        setStudyHandoff({ ...consentOffer, studies: chosen, askConsent: false });
      }
    } catch (error) {
      setEnrollError(error?.message || 'Could not enroll this patient.');
    } finally {
      setEnrolling(false);
    }
  }, [consentOffer, currentPatient]);

  const leaveStudy = (nextStudy, surgeryOnly) => {
    flushOpenDemoCharts();
    const enteringProspect = nextStudy === 'compass' || nextStudy === 'alliance';
    if (!(showCompass && enteringProspect)) prospectOfferSeq.current += 1;
    setStudyHandoff(null);
    setConsentOffer(null);
    setStudy(nextStudy);
    setShowSurgeryOnly(surgeryOnly);
  };

  useEffect(() => {
    if (study === 'compass' || study === 'alliance') prospectStudyRef.current = study;
  }, [study]);

  const openProspect = () => {
    if (showCompass) return;
    leaveStudy(prospectStudyRef.current || 'alliance', false);
  };

  // Switch to Med Management (red) palette when that list is showing
  useEffect(() => {
    if (showSurgeryOnly) {
      document.body.classList.add('med-management-mode');
    } else {
      document.body.classList.remove('med-management-mode');
    }
    return () => document.body.classList.remove('med-management-mode');
  }, [showSurgeryOnly]);

  const handlePatientApproval = (patientId, appointmentId, status) => {
    const aid = appointmentId != null && appointmentId !== '' ? String(appointmentId) : null;
    const patchRow = (row) => {
      if (!row || row.id !== patientId) return row;
      if (aid != null && row.appointment_id != null && String(row.appointment_id) !== aid) {
        return row;
      }
      return { ...row, approval_status: status };
    };
    setAllPatients((prev) => prev.map(patchRow));
    setCurrentPatient((prev) => (prev ? patchRow(prev) : prev));
  };

  const handleJdDecisionApproved = (patientId) => {
    if (!patientId) return;
    setAllPatients((prev) =>
      (prev || []).map((row) => (
        row?.id === patientId ? { ...row, jd_approval_status: 'approved' } : row
      )),
    );
    setCurrentPatient((prev) => (
      prev?.id === patientId ? { ...prev, jd_approval_status: 'approved' } : prev
    ));
  };

  // Optional: Show loading spinner while checking auth state
  if (loading) {
    return <LoadingSpinner text="Loading..." />;
  }

  return (
    <Router>
      <div style={{ minHeight: '100dvh', height: '100dvh', display: 'flex', flexDirection: 'column' }}>
        <Helmet>
          <title>Springhouse Analytics</title>
        </Helmet>

        <LoginModal
          show={showLoginModal}
          onHide={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
          databaseMode={databaseMode}
          onDatabaseModeChange={handleDatabaseModeChange}
        />

      <Navbar
        style={{
          backgroundColor: 'var(--navbar-bg)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1030,
          transition: 'background-color 0.2s ease',
          paddingBlock: '0.55vh'
        }}
        variant="light"
        expand="lg"
        className="mb-2"
      >
        <Container>
          <Navbar.Brand as={Link} to="/" onClick={() => { flushOpenDemoCharts(); setCurrentPatient(null); setStudyHandoff(null); setConsentOffer(null); setStudy(''); }}>
            <img
              src={logo}
              style={{
                height: 'min(4.5vh, 2.35rem)',
                objectFit: 'contain'
              }}
              className="d-inline-block"
              alt="SpringHouse Logo"
            /> Springhouse Analytics
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
            <Nav>
              {isLoggedIn ? (
                <>
                  <div className="d-flex align-items-center me-3">
                    <button
                      type="button"
                      onClick={() => leaveStudy('', false)}
                      style={navTabStyle(!showCompass && !showSurgeryOnly)}
                    >
                      Joint Decisions
                    </button>
                    <button
                      type="button"
                      onClick={openProspect}
                      style={navTabStyle(showCompass)}
                    >
                      Prospect
                    </button>
                    <button
                      type="button"
                      onClick={() => leaveStudy('', true)}
                      style={navTabStyle(!showCompass && showSurgeryOnly)}
                    >
                      Med Management
                    </button>
                  </div>
                  <NavDropdown
                    title={
                      <span>
                        {user.email}
                        {databaseMode === DATABASE_MODES.DEMO && (
                          <span className="badge bg-secondary ms-2">Demo</span>
                        )}
                      </span>
                    }
                    id="user-dropdown"
                  >
                    <NavDropdown.Item as={Link} to="/profile">Profile</NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.ItemText>
                      <div className="px-1 py-1">
                        <div className="text-muted small mb-1">Database</div>
                        <DatabaseModeToggle
                          value={databaseMode}
                          onChange={handleDatabaseModeChange}
                        />
                      </div>
                    </NavDropdown.ItemText>
                    <NavDropdown.Divider />
                    <NavDropdown.Item onClick={handleLogout}>Log Out</NavDropdown.Item>
                  </NavDropdown>
                </>
              ) : (
                <Nav.Link onClick={() => setShowLoginModal(true)}>Log In</Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

        <Container
          fluid
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            overflow: 'hidden',
            boxSizing: 'border-box',
            paddingTop: '0.5vh',
            paddingBottom: '0.5rem',
          }}
        >
          {showCompass && !isLoggedIn ? (
            study === 'alliance' ? <AllianceWorkspace /> : <CompassWorkspace />
          ) : isLoggedIn ? (
            <PatientsListTab
              allPatients={allPatients}
              currentPatient={currentPatient}
              loadCurrentPatient={loadCurrentPatient}
              showCompass={showCompass}
              study={study}
              onPatientApproval={handlePatientApproval}
              showSurgeryOnly={showSurgeryOnly}
              studyHandoff={studyHandoff}
              onOfferStudyConsent={(handoff) => {
                setEnrollError('');
                setConsentOffer(handoff);
              }}
              onGoToMedManagement={() => {
                flushOpenDemoCharts();
                setStudyHandoff(null);
                setStudy('');
                setShowSurgeryOnly(true);
              }}
              onPatientListRefresh={refreshPatientList}
              onJdDecisionApproved={handleJdDecisionApproved}
              blurPatientNames={blurPatientNames}
              onBlurPatientNamesChange={setBlurPatientNames}
              isPatientListLoading={isPatientListLoading}
              pipelineReload={pipelineReload}
              enrollmentSurgeryPrompt={enrollmentSurgeryPrompt}
              onStudyChange={(nextStudy) => leaveStudy(nextStudy, false)}
            />
          ) : (
            <div className="text-center mt-5">
              <h2>Welcome to Springhouse Analytics</h2>
              <p>Please log in to view patient information.</p>
              <Button variant="primary" onClick={() => setShowLoginModal(true)}>
                Log In
              </Button>
            </div>
          )}
        </Container>
        <StudyConsentDialog
          handoff={consentOffer}
          onAccept={acceptStudyEnrollment}
          onDecline={() => {
            if (enrolling) return;
            dismissedOfferKey.current = prospectOfferKey.current;
            setEnrollError('');
            setConsentOffer(null);
          }}
          error={enrollError}
          busy={enrolling}
          hasScheduledSurgery={Boolean(consentOffer?.surgeryDate) || patientHasScheduledSurgery(currentPatient, allPatients)}
          onNeedSurgery={() => {
            setEnrollError('');
            setConsentOffer(null);
            setEnrollmentSurgeryPrompt((value) => value + 1);
          }}
        />
      </div>
    </Router>
  );
}

export default App;
